/**
 * Feed-URL-Helpers (calendar-v2 3.1)
 *
 * Der Backend baut die feed_url aus BACKEND_URL (Dev: http://localhost:8000).
 * Diese URL ist von anderen Geräten (QR-Scan mit Handy) nicht erreichbar und
 * in Produktion liegen Frontend und Backend auf unterschiedlichen Origins.
 * Daher wird die URL client-seitig auf die tatsächlich genutzte API-Basis
 * «re-based»: gleiche Basis wie axios client.ts (VITE_API_URL, in Dev leer →
 * window.location.origin, da Vite /api auf den Backend proxied). Der Pfad der
 * Server-URL (inkl. vollem Token) bleibt unverändert übernommen.
 *
 * Edge (calendar-v3 I10): BACKEND_URL und VITE_API_URL dürfen nicht beide
 * denselben Path-Prefix tragen (z.B. '/backend') — der Pfad wird 1:1 aus der
 * Server-URL übernommen und an die Basis gehängt, ein gemeinsamer Prefix
 * würde sich dadurch verdoppeln. In diesem Setup VITE_API_URL ohne Pfad
 * konfigurieren.
 */

export function getApiBase(): string {
  const base = (import.meta.env.VITE_API_URL || window.location.origin) as string
  return base.replace(/\/+$/, '')
}

export function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]'
}

// calendar-v6 (QR): Der iPhone-QR läuft bewusst über http im LAN (Port 5174,
// HTTP-Spiegel-Instanz) — iOS Kalender akzeptiert http-Feeds für lokale IPs
// und braucht so kein Zertifikat/Profil. Der Mac-Button nutzt dagegen https
// (Apple Kalender auf macOS verlangt TLS).
export const DEV_HTTP_MIRROR_PORT = 5174

export function rebaseFeedUrl(
  serverUrl: string | null | undefined,
  machineIp?: string | null
): string | null {
  if (!serverUrl) return null
  try {
    const pathname = new URL(serverUrl).pathname
    if (!pathname || !pathname.endsWith('.ics')) {
      return serverUrl
    }
    let base = getApiBase()
    // calendar-v4: Wurde die Seite per localhost geöffnet (Dev), ist die
    // Origin-Basis für andere Geräte (QR-Scan mit Handy) unerreichbar —
    // localhost zeigt dort auf das Gerät selbst. Das Backend liefert in Dev
    // seine LAN-IP (machine_ip); damit wird der QR auch dann nutzbar, wenn
    // der Browser nicht explizit über die LAN-IP geöffnet wurde. Greift nur
    // ohne VITE_API_URL (reines Dev-Setup) — in Produktion zählt die
    // konfigurierte öffentliche API-Basis.
    if (!import.meta.env.VITE_API_URL && isLoopbackHost(window.location.hostname) && machineIp) {
      // calendar-v6: http über den Spiegel-Port (5174), nicht den https-Port
      // der Seite — so akzeptiert iOS den Feed ohne Zertifikat.
      base = `http://${machineIp}:${DEV_HTTP_MIRROR_PORT}`
    }
    return `${base}${pathname}`
  } catch {
    return serverUrl
  }
}

export function toWebcal(url: string): string {
  return url.replace(/^https?:\/\//i, 'webcal://')
}

export function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, 'https://')
}

export function toAppleCalendarUrl(url: string | null | undefined): string | null {
  // calendar-v6: Apple Kalender (macOS/iOS) verlangt für Abos zwingend TLS.
  // webcals:// wird von Safari bei JS-Navigation mit «address is invalid»
  // abgelehnt — webcal:// auf der https-Basis funktioniert dagegen zuver-
  // lässig (Apple upgraded webcal automatisch auf https). Dev: Vite läuft
  // mit mkcert-Zertifikat auf dem gleichen Port, Produktion ist ohnehin https.
  if (!url) return null
  try {
    const httpsUrl = toHttps(url)
    new URL(httpsUrl)
    return toWebcal(httpsUrl)
  } catch {
    return null
  }
}

export function isWebcalSupported(): boolean {
  // webcal:// wird nur zuverlässig übergeben, wenn der Browser einen
  // Handler für das Protokoll kennt: Safari (macOS/iOS, öffnet die
  // Kalender-App). Chromium- und Firefox-Browser haben keinen eingebauten
  // webcal-Handler und zeigen sonst die Fehlerseite
  // «The request for webcal://... failed».
  const ua = navigator.userAgent.toLowerCase()
  const isChromium = /chrome|chromium|crios|edg|opr|yabrowser/i.test(ua)
  const isFirefox = /firefox|fxios/i.test(ua)
  return /safari|webkit/i.test(ua) && !isChromium && !isFirefox
}
